import { coreApi } from '.';
import { TAG_TYPES } from 'types/services';
import type {
  CopyUserRolesReq,
  BulkModifyUsersRolesReq,
  CreateUserReq,
  CreateOrUpdateUserRes,
  GetAllUserReq,
  GetAllUserRes,
  GetSimpleUserListReq,
  GetSimpleUserListRes,
  GetUserByIdReq,
  GetUserByIdRes,
  GetUserNotificationsReq,
  GetUserNotificationsRes,
  GetUserRolesWithPermissionsRes,
  PatchRoleStatusReq,
  StartImpersonationReq,
  StartImpersonationRes,
  StopImpersonationRes,
  UpdateUserReq,
  UpdateUserRolesReq,
  UpdateUserRolesValidityReq,
  UpdateUserStatusReq,
  UpdateUserStatusRes,
  UpdateUsersStatusReq,
  GetUsersByIdSimpleRes,
  GetUsersByIdSimpleReq,
  UserSubstitutionItem,
  UserSubstitutionsRes,
  UserSubstitutionsReq,
  UserRolesWithTagsRes,
  UserRolesWithTagsReq,
  CreateUserSubstitutionReq,
  CreateUserSubstitutionRes,
  UpdateUserSubstitutionReq,
  UpdateUserSubstitutionRes,
  UserSubstitutionsAsSubstituteRes,
  StartSubstitutionRes,
  StartSubstitutionReq,
  UpdateUserWithoutAdminReq,
} from 'types';
import type { userDetailDataScheme, usersDataScheme } from 'utils/table/admin/core/users';
import type { usersDataScheme as simpleUsersDataScheme } from 'utils/table/user/users';

export const userService = coreApi.injectEndpoints({
  overrideExisting: true,

  endpoints: (builder) => ({
    createUser: builder.mutation<CreateOrUpdateUserRes, CreateUserReq>({
      query: (data) => ({
        url: 'Users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USERS],
    }),
    getUserById: builder.query<GetUserByIdRes, GetUserByIdReq<typeof userDetailDataScheme>>({
      query: (data) => ({
        url: `Users/${data.id}`,
        method: 'GET',
        params: {
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search,
        },
      }),
      providesTags: [TAG_TYPES.USER_DETAIL],
    }),
    getUsersByIdSimple: builder.query<GetUsersByIdSimpleRes, GetUsersByIdSimpleReq<typeof simpleUsersDataScheme>>({
      query: (data) => ({
        url: `Users/Simple/List`,
        method: 'POST',
        body: {
          UserIds: data.userIds,
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search,
        },
      }),
      providesTags: [TAG_TYPES.USERS],
    }),
    updateUser: builder.mutation<CreateOrUpdateUserRes, UpdateUserReq>({
      query: ({ id, data }) => ({
        url: `Users/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USERS, TAG_TYPES.USER_DETAIL],
    }),
    updateUserWithoutAdmin: builder.mutation<CreateOrUpdateUserRes, UpdateUserWithoutAdminReq>({
      query: (data) => ({
        url: 'Users/Me',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USERS, TAG_TYPES.USER_DETAIL],
    }),
    getAllUsers: builder.query<GetAllUserRes, GetAllUserReq<typeof usersDataScheme>>({
      query: (data) => ({
        url: 'Users',
        method: 'GET',
        params: {
          DateOfDeactivation: data.DateOfDeactivation,
          ActiveFrom: data.DateOfActivation,
          Email: data.Email,
          ContactEmail: data.ContactEmail,
          Reference: data.Reference,
          FullName: data.FullName,
          StatusList: data.Status,
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search ?? '',
          Permission: data.Permission ?? [],
        },
      }),
      providesTags: [TAG_TYPES.USERS],
    }),
    getSimpleUsersByRoles: builder.query<GetSimpleUserListRes, GetSimpleUserListReq<typeof usersDataScheme>>({
      query: (data) => ({
        url: 'Users/Roles/SimpleUsers',
        method: 'POST',
        body: { ids: data.ids },
        params: {
          Page: data.page,
          PageSize: data.pageSize,
          OrderBy: data.orderBy,
          OrderDesc: data.orderDesc,
          Search: data.search ?? '',
        },
      }),
      providesTags: [TAG_TYPES.USERS],
    }),
    updateUserStatus: builder.mutation<UpdateUserStatusRes, UpdateUserStatusReq>({
      query: ({ id, status }) => ({
        url: `Users/${id}/Status`,
        method: 'PUT',
        body: status,
      }),
      invalidatesTags: [TAG_TYPES.USERS, TAG_TYPES.USER_DETAIL],
    }),
    updateUsersStatus: builder.mutation<void, UpdateUsersStatusReq>({
      query: (data) => ({
        url: 'Users/Status',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USERS, TAG_TYPES.USER_DETAIL],
    }),
    bulkModifyUsersRoles: builder.mutation<void, BulkModifyUsersRolesReq>({
      query: (data) => ({
        url: 'Users/Roles/Bulk',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USERS, TAG_TYPES.USER_DETAIL, TAG_TYPES.ROLES, TAG_TYPES.ROLE_DETAIL],
    }),
    startImpersonation: builder.query<StartImpersonationRes, StartImpersonationReq>({
      query: ({ id }) => ({
        url: 'Users/StartImpersonation',
        method: 'POST',
        params: {
          userId: id,
        },
      }),
    }),
    stopImpersonation: builder.query<StopImpersonationRes, void>({
      query: () => ({
        url: 'Auth/StopImpersonation',
        method: 'POST',
      }),
    }),
    updateUserRoles: builder.mutation<void, UpdateUserRolesReq>({
      query: ({ id, data }) => ({
        url: `Users/${id}/Roles`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_DETAIL, TAG_TYPES.USERS],
    }),
    copyUserRoles: builder.mutation<void, CopyUserRolesReq>({
      query: ({ id, data }) => ({
        url: `Users/${id}/Roles`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_DETAIL, TAG_TYPES.USERS],
    }),
    updateUserRolesValidity: builder.mutation<void, UpdateUserRolesValidityReq>({
      query: ({ roleId, data }) => ({
        url: `Users/Roles/${roleId}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_DETAIL, TAG_TYPES.USERS, TAG_TYPES.ROLES, TAG_TYPES.ROLE_DETAIL],
    }),
    getUserNotifications: builder.query<GetUserNotificationsRes, GetUserNotificationsReq>({
      query: ({ id, page, pageSize }) => ({
        url: `Users/${id}/Notifications`,
        method: 'GET',
        params: {
          Page: page,
          PageSize: pageSize,
        },
      }),
      providesTags: [TAG_TYPES.USER_NOTIFICATIONS],
    }),
    removeRolesBasedOnUser: builder.mutation<
      void,
      {
        id: string;
        data: string[];
      }
    >({
      query: ({ id, data }) => ({
        url: `Users/${id}/Roles`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_DETAIL, TAG_TYPES.USERS],
    }),
    patchRoleStatus: builder.mutation<void, PatchRoleStatusReq>({
      query: ({ id, data }) => ({
        url: `Users/${id}/Roles`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_DETAIL, TAG_TYPES.USERS],
    }),
    getCurrentUserRoles: builder.query<{ roles: { id: string }[] }, void>({
      query: () => ({
        url: 'Users/Me/Roles',
        method: 'GET',
      }),
    }),
    getCurrentUserRolesWithPermissions: builder.query<GetUserRolesWithPermissionsRes, void>({
      query: () => ({
        url: 'Users/Me/Roles/Permissions',
        method: 'GET',
      }),
    }),
    getCurrentUserRolesWithTags: builder.query<UserRolesWithTagsRes, Partial<UserRolesWithTagsReq>>({
      query: (data) => ({
        url: 'Users/Me/Roles/Tags',
        method: 'POST',
        body: {
          validFrom: data.validFrom,
          validTo: data.validTo,
          tags: data.tags,
        },
        params: {
          page: data.page,
          pageSize: data.pageSize,
          orderBy: data.orderBy,
          orderDesc: data.orderDesc,
          search: data.search,
        },
      }),
    }),
    getUserRolesWithTagsById: builder.query<UserRolesWithTagsRes, Partial<UserRolesWithTagsReq> & { id: string }>({
      query: (data) => ({
        url: `Users/${data.id}/Roles/Tags`,
        method: 'POST',
        body: {
          validFrom: data.validFrom,
          validTo: data.validTo,
          tags: data.tags,
        },
        params: {
          page: data.page,
          pageSize: data.pageSize,
          orderBy: data.orderBy,
          orderDesc: data.orderDesc,
          search: data.search,
        },
      }),
    }),
    getCurrentUserSubstitutions: builder.query<UserSubstitutionsRes, UserSubstitutionsReq>({
      query: (params) => ({
        url: 'Users/Me/Substitutions',
        method: 'GET',
        params,
      }),
      providesTags: [TAG_TYPES.USER_SUBSTITUTIONS],
    }),
    getAllUsersSubstitutions: builder.query<UserSubstitutionsRes, UserSubstitutionsReq>({
      query: (params) => ({
        url: 'Users/Substitutions',
        method: 'GET',
        params,
      }),
      providesTags: [TAG_TYPES.USER_SUBSTITUTIONS],
    }),
    getCurrentUserSubstitutionById: builder.query<UserSubstitutionItem, { id: string }>({
      query: ({ id }) => ({
        url: `Users/Me/Substitutions/${id}`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.USER_SUBSTITUTION_DETAIL],
    }),
    getSubstitutionById: builder.query<UserSubstitutionItem, { id: string }>({
      query: ({ id }) => ({
        url: `Users/Substitutions/${id}`,
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.USER_SUBSTITUTION_DETAIL],
    }),
    createUserSubstitution: builder.mutation<CreateUserSubstitutionRes, CreateUserSubstitutionReq>({
      query: ({ userId, ...data }) => ({
        url: `Users/${userId}/Substitutions/Create`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_SUBSTITUTIONS, TAG_TYPES.USER_SUBSTITUTION_DETAIL],
    }),
    updateUserSubstitution: builder.mutation<UpdateUserSubstitutionRes, UpdateUserSubstitutionReq>({
      query: ({ id, ...data }) => ({
        url: `Users/Substitutions/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_SUBSTITUTIONS, TAG_TYPES.USER_SUBSTITUTION_DETAIL],
    }),
    getCurrentUserSubstitutionsAsSubstitute: builder.query<UserSubstitutionsAsSubstituteRes, UserSubstitutionsReq>({
      query: (params) => ({
        url: 'Users/Me/Substitutions/Substitute',
        method: 'GET',
        params,
      }),
      providesTags: [TAG_TYPES.USER_SUBSTITUTIONS_AS_SUBSTITUTE],
    }),
    startSubstitution: builder.query<StartSubstitutionRes, StartSubstitutionReq>({
      query: ({ substitutionId }) => ({
        url: `Users/Substitutions/${substitutionId}/Start`,
        method: 'POST',
      }),
    }),
    deleteSubstitutions: builder.mutation<void, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `Users/Substitutions`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: [TAG_TYPES.USER_SUBSTITUTIONS],
    }),
    deleteKeycloakSubject: builder.mutation<void, string>({
      query: (id) => ({
        url: `Users/${id}/KeycloakSubject`,
        method: 'DELETE',
      }),
    }),
  }),
});
export const {
  useGetAllUsersQuery,
  useGetSimpleUsersByRolesQuery,
  useCopyUserRolesMutation,
  useGetUserByIdQuery,
  useGetUsersByIdSimpleQuery,
  useLazyGetUsersByIdSimpleQuery,
  useGetUserNotificationsQuery,
  useCreateUserMutation,
  useLazyGetAllUsersQuery,
  useLazyGetUserByIdQuery,
  useLazyGetUserNotificationsQuery,
  useLazyStartImpersonationQuery,
  useLazyStopImpersonationQuery,
  useUpdateUserStatusMutation,
  useUpdateUsersStatusMutation,
  useBulkModifyUsersRolesMutation,
  useStartImpersonationQuery,
  useUpdateUserMutation,
  useUpdateUserWithoutAdminMutation,
  useUpdateUserRolesMutation,
  useUpdateUserRolesValidityMutation,
  useRemoveRolesBasedOnUserMutation,
  usePatchRoleStatusMutation,
  useGetCurrentUserRolesWithTagsQuery,
  useCreateUserSubstitutionMutation,
  useUpdateUserSubstitutionMutation,
  useGetCurrentUserSubstitutionsQuery,
  useLazyGetCurrentUserSubstitutionsQuery,
  useGetCurrentUserSubstitutionByIdQuery,
  useLazyGetCurrentUserSubstitutionByIdQuery,
  useGetCurrentUserSubstitutionsAsSubstituteQuery,
  useStartSubstitutionQuery,
  useLazyStartSubstitutionQuery,
  useGetSubstitutionByIdQuery,
  useLazyGetSubstitutionByIdQuery,
  useGetAllUsersSubstitutionsQuery,
  useLazyGetAllUsersSubstitutionsQuery,
  useDeleteSubstitutionsMutation,
  useDeleteKeycloakSubjectMutation,
  useGetCurrentUserRolesQuery,
  useLazyGetCurrentUserRolesQuery,
  useGetUserRolesWithTagsByIdQuery,
  useLazyGetUserRolesWithTagsByIdQuery,
  useGetCurrentUserRolesWithPermissionsQuery,
  useLazyGetCurrentUserRolesWithPermissionsQuery,
} = userService;
